export type PropertyOwnershipStatus = {
  insuranceRequired: boolean
  isFuture: boolean
}

const INSURANCE_LOOKBACK_DAYS = 90
const MS_PER_DAY = 24 * 60 * 60 * 1000

/** Midnight for the given date, so only whole calendar days are compared. */
const startOfDay = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/**
 * A complete mm/dd/yyyy ownership date. An incomplete or impossible value,
 * such as a date still being typed, is not a date yet.
 */
const parseOwnershipDate = (value: string): Date | undefined => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim())
  if (!match) {
    return undefined
  }

  const month = Number(match[1])
  const day = Number(match[2])
  const year = Number(match[3])
  const date = new Date(year, month - 1, day)
  const isSameCalendarDay = date.getFullYear() === year
    && date.getMonth() === month - 1
    && date.getDate() === day

  return isSameCalendarDay ? date : undefined
}

/**
 * Insurance is collected once the property has been owned for 90 days or
 * fewer. A later date is in the future and is not a valid ownership date.
 * `asOf` is the caller's current date (browser in the UI, server on submit).
 */
export const propertyOwnershipStatus = (
  ownershipDate: string,
  asOf: Date,
): PropertyOwnershipStatus => {
  const parsedOwnershipDate = parseOwnershipDate(ownershipDate)
  if (!parsedOwnershipDate) {
    return {
      isFuture: false,
      insuranceRequired: false,
    }
  }

  const daysSinceOwnership = Math.round(
    (startOfDay(asOf).getTime() - startOfDay(parsedOwnershipDate).getTime()) / MS_PER_DAY,
  )

  return {
    isFuture: daysSinceOwnership < 0,
    insuranceRequired: daysSinceOwnership >= 0 && daysSinceOwnership <= INSURANCE_LOOKBACK_DAYS,
  }
}
